import React, { useState, useEffect } from "react";
import ReactDOM from 'react-dom';
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import config from "../../config";
import NewOrganizationModal from "./NewOrganizationModal";
import { Shipment } from "../../types/shipment.types";
import { fetchAllCampaigns, fetchAllOrganizations } from "../../services/CampaignService";
import { createShipment } from "../../services/DonationService";
import { showSuccessToast, showErrorToast } from "../../lib/ToastUtility";
import { Button } from "../Button";

const GOOGLE_MAPS_API_KEY = config.googleMapsApiKey;

const mapContainerStyle = {
    width: "100%",
    height: "192px",
};

const defaultCenter = {
    lat: 40.7128,
    lng: -74.0060,
};

const LIBRARIES: ("places" | "maps")[] = ["maps", "places"]; // Define outside component

interface NewShipmentModalProps {
  show: boolean;
  onClose: () => void;
  onSuccess: (shipment: any) => void;
  donations: any[];
}

const NewShipmentModal: React.FC<NewShipmentModalProps> = ({
  show,
  onClose,
  onSuccess,
  donations,
}) => {
    const [shipment, setShipment] = useState<any>({
        id: "",
        location: "",
        name: "",
        campaign: "",
        item: "Boxes",
        quantity: "",
        organization: "",
        logistics: "",
        notifyDonors: true,
    });
    const [loading, setLoading] = useState(false);

    // State for organizations and modal
    const [organizations, setOrganizations] = useState<Array<{ name: string; url: string; id?: string }>>([]);
    const [showOrgModal, setShowOrgModal] = useState(false);

    // State for campaigns
    const [campaigns, setCampaigns] = useState<any[]>([]);
    useEffect(() => {
        if (show) {
            fetchAllCampaigns("true").then(res => {
                if (res && res.data) setCampaigns(res.data);
            });
        }
    }, [show]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [campaignError, setCampaignError] = useState("");

    // Google Maps loader
    const { isLoaded } = useJsApiLoader({
      googleMapsApiKey: GOOGLE_MAPS_API_KEY,
      libraries: LIBRARIES,
    });

    // Add geocoding function
    const geocodeAddress = async (address: string, apiKey: string) => {
        const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
        );

        const data = await response.json();
        if (data.status === "OK" && data.results.length > 0) {
            return data.results;
        }
        throw new Error("Location not found");
    };

    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState("");
    const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number } | null>(null);
    const [locationData, setLocationData] = useState<{
      address: string;
      latitude: string;
      longtitude: string;
    } | null>(null);

    // Fetch all campaigns
    useEffect(() => {
        const loadCampaigns = async () => {
            setLoadingCampaigns(true);
            setCampaignError("");
            try {
                const res = await fetchAllCampaigns();
                if (res && res.data) setCampaigns(res.data);
            } catch (error) {
                setCampaignError("Error loading campaigns");
            } finally {
                setLoadingCampaigns(false);
            }
        };

        loadCampaigns();
    }, []);

    // Fetch all organizations
    useEffect(() => {
        const loadOrganizations = async () => {
            try {
                const response = await fetchAllOrganizations();
                setOrganizations(response?.data || []);
            } catch (err) {
                setOrganizations([]);
            }
        };
        loadOrganizations();
    }, []);

    if (!show) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setShipment((prev: any) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

   const handleConfirm = async () => {
        const shipmentPayload = {
            name: shipment.name,
            label: "Urgent",
            currentStutus: "CREATED",
            orderedBoxUnit: parseInt(shipment.quantity),
            notifyDoner: shipment.notifyDonors,
            active: true,
            orgenization: shipment.organization,
            languageCode: "EN",
            address: locationData?.address || shipment.address,
            latitude: locationData?.latitude || shipment.latitude,
            longtitude: locationData?.longtitude || shipment.longtitude,
            ShipmentsOnDonations: {
            "create": donations.map((donation) => ({
                donation: {
                    connect: { id: donation.id }
                }
            }))
            }
        }

        setLoading(true);
        try {
            const response = await createShipment(shipmentPayload);
            if (response && response.data) {
                // Pass success response to parent
                if (onSuccess) {
                    showSuccessToast('Shipment assigned successfully!');
                    onSuccess(response.data);
                    onClose();
                }
            }
        } catch (err) {
            showErrorToast('Shipment assignment failed!');
        } finally {
            setLoading(false);
        }
    };

    // Add new organization and select it (wait for backend org object)
    const handleAddOrganization = (org: { name: string; url: string }) => {
        setOrganizations((prev: any) => [...prev, org]);
        setShipment((prev: Shipment) => ({
            ...prev,
            organization: org.name,
        }));
    };

    // Add search handler
    const handleLocationSearch = async () => {
        setSearching(true);
        setSearchError("");
        try {
            const results = await geocodeAddress(shipment.location, GOOGLE_MAPS_API_KEY);

            if (results && results.length > 0) {
                const place = results[0];
                const address = place.formatted_address;
                const latitude = place.geometry.location.lat;
                const longtitude = place.geometry.location.lng;
                setMapCenter({ lat: latitude, lng: longtitude });
                setLocationData({
                  address,
                  latitude: latitude.toString(),
                  longtitude: longtitude.toString(),
                });
                setMarkerPosition({ lat: latitude, lng: longtitude });
            } else {
                setSearchError("Location not found");
            }
        } catch (err) {
            setSearchError("Location not found");
            setLocationData({
                  address: "",
                  latitude: "",
                  longtitude: "",
                });
        } finally {
            setSearching(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-white/50 dark:border-white/10 shadow-lg w-full max-w-4xl">
                <div className="flex justify-between items-center border-b dark:border-white/10 px-8 py-6">
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">New shipment</h2>
                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-3xl"
                    >
                        &times;
                    </Button>
                </div>
                <div className="px-8 py-6">
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <div className="mb-4">
                                <label className="block text-gray-700 dark:text-gray-300 mb-1">ID</label>
                                <input
                                    name="id"
                                    value={shipment.id}
                                    onChange={handleChange}
                                    className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                    placeholder="Shipment ID"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 dark:text-gray-300 mb-1">Name <span title="Shipment name">ℹ️</span></label>
                                <input
                                    name="name"
                                    value={shipment.name}
                                    onChange={handleChange}
                                    className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                    placeholder="Shipment name"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 dark:text-gray-300 mb-1">Select campaign</label>
                                <select
                                    name="campaign"
                                    value={shipment.campaign}
                                    onChange={handleChange}
                                    className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                >
                                    <option value="">Select campaign</option>
                                    {loadingCampaigns ? (
                                        <option value="">Loading campaigns...</option>
                                    ) : campaignError ? (
                                        <option value="">Error loading campaigns</option>
                                    ) : (
                                        campaigns.map((campaign) => (
                                            <option key={campaign.id} value={campaign.id}>
                                                {campaign.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                            <div className="mb-4 flex gap-2">
                                <div className="flex-1">
                                    <label className="block text-gray-700 dark:text-gray-300 mb-1">Item</label>
                                    <select
                                        name="item"
                                        value={shipment.item}
                                        onChange={handleChange}
                                        className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                        disabled
                                    >
                                        <option value="Boxes">Boxes</option>
                                        <option value="Bars">Bars</option>
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-gray-700 dark:text-gray-300 mb-1">Ordered quantity</label>
                                    <input
                                        name="quantity"
                                        value={shipment.quantity}
                                        onChange={handleChange}
                                        className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                        placeholder="000"
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="mb-4">
                                <label className="block text-gray-700 dark:text-gray-300 mb-1">Location</label>
                                <input
                                    name="location"
                                    value={shipment.location}
                                    onChange={handleChange}
                                    className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                    placeholder="Location"
                                />
                            </div>
                            <Button
                                variant="primary"
                                size="md"
                                type="button"
                                onClick={handleLocationSearch}
                                disabled={searching}
                            >
                                Search Location
                            </Button>
                            {searchError && <div className="text-red-600 mb-2">{searchError}</div>}
                            <div className="w-full h-48 bg-gray-200 dark:bg-slate-700 rounded overflow-hidden mt-4">
                                {isLoaded ? (
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        center={mapCenter}
                                        zoom={10}
                                    >
                                        {markerPosition && <Marker position={markerPosition} />}
                                    </GoogleMap>
                                ) : (
                                    <div className="h-full w-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                                        Loading map...
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Organization and Logistic details moved below and full width */}
                    <div className="mt-6">
                        <div className="mb-4">
                            <label className="block text-gray-700 dark:text-gray-300 mb-1">Organization</label>
                            <div className="flex">
                                <select
                                    name="organization"
                                    value={shipment.organization}
                                    onChange={handleChange}
                                    className="border rounded px-3 py-2 flex-1 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                >
                                    <option value="">Select organization</option>
                                    {organizations.map((org, idx) => (
                                        <option key={org.id || idx} value={org.name}>{org.name}</option>
                                    ))}
                                </select>
                                <Button
                                    variant="secondary"
                                    size="md"
                                    type="button"
                                    className="ml-4"
                                    onClick={() => setShowOrgModal(true)}
                                >
                                    Add new organization
                                </Button>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 dark:text-gray-300 mb-1">
                                Logistic details <span title="Tracking link or message">ℹ️</span>
                            </label>
                            <input
                                name="logistics"
                                value={shipment.logistics}
                                onChange={handleChange}
                                className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                placeholder="Tracking link or message"
                            />
                        </div>
                        <div className="mb-4 flex items-center">
                            <input
                                type="checkbox"
                                name="notifyDonors"
                                checked={shipment.notifyDonors}
                                onChange={handleChange}
                                className="mr-2 dark:border-white/10 dark:bg-slate-800"
                            />
                            <label className="text-gray-700 dark:text-gray-300">Notify donors</label>
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 px-8 py-6 border-t dark:border-white/10">
                    <Button
                        variant="secondary"
                        size="md"
                        type="button"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        type="button"
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading ? "Confirming..." : "Confirm"}
                    </Button>
                </div>
            </div>
            {/* New Organization Modal */}
            <NewOrganizationModal
                open={showOrgModal}
                onClose={() => setShowOrgModal(false)}
                onConfirm={handleAddOrganization}
            />
        </div>
    ,
    document.body
  );
};

export default NewShipmentModal;
